import { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const testimonials = [
  { name: "Rosario Garcia", text: "Beautiful work done on my Lamborghini Urus, would definitely recommend to anyone local looking for a professional detail.", vehicle: "Lamborghini Urus" },
  { name: "Robbie Bauder", text: "My car looks brand new — the paint is gleaming, the interior smells fresh, and every tiny nook and cranny is spotless.", vehicle: "Full Detail" },
  { name: "Kingston Vo", text: "MetroShine did a great job on my vehicle. Highly recommend this service 👍", vehicle: "Interior Detail" },
  { name: "Seif Hekal", text: "Not a single spec of dirt or water or stain was found on the car inside and outside. Absolutely recommend.", vehicle: "Full Detail" },
  { name: "Morgan Vaught", text: "His work is crisp and the car looks great! I will be using them in the future!", vehicle: "Exterior Detail" },
  { name: "Faisal Khaiser", text: "They really care about the customer's car. Definitely know how to detail correctly.", vehicle: "Interior Detail" },
  { name: "Kareem Nweashe", text: "Amazing service, took care of my paint and carbon ceramic brakes, car looks great now!", vehicle: "Paint Correction" },
];

const TestimonialCarousel = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const next = () => { setDirection(1); setCurrent((p) => (p + 1) % testimonials.length); };
  const prev = () => { setDirection(-1); setCurrent((p) => (p - 1 + testimonials.length) % testimonials.length); };

  useEffect(() => {
    intervalRef.current = setInterval(next, 5000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const resetInterval = (fn: () => void) => () => {
    clearInterval(intervalRef.current);
    fn();
    intervalRef.current = setInterval(next, 5000);
  };

  const t = testimonials[current];

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 60 : -60, opacity: 0, scale: 0.96, filter: "blur(4px)" }),
    center: { x: 0, opacity: 1, scale: 1, filter: "blur(0px)" },
    exit: (dir: number) => ({ x: dir > 0 ? -60 : 60, opacity: 0, scale: 0.96, filter: "blur(4px)" }),
  };

  return (
    <section className="section-padding bg-secondary overflow-hidden relative">
      <div className="absolute inset-0 bg-dots opacity-8" />
      <div className="absolute inset-0 mesh-gradient" />
      <div className="orb-glow orb-gold w-[500px] h-[500px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-8 orb-pulse" />
      
      <div className="container-luxury max-w-4xl text-center relative">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="badge-shimmer mb-4 inline-flex">What Clients Say</span>
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-16">
            <span className="gold-gradient-text">Testimonials</span>
          </h2>
        </motion.div>

        <div className="relative min-h-[320px] flex items-center justify-center">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={current}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
              className="px-4 absolute w-full"
            >
              {/* Quote icon with ring */}
              <motion.div className="flex justify-center mb-8" initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.2, type: "spring", stiffness: 300 }}>
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-primary/8 flex items-center justify-center border border-primary/15">
                    <Quote size={24} className="text-primary" />
                  </div>
                  <div className="absolute inset-0 rounded-full border border-primary/10 animate-breathe scale-125" />
                </div>
              </motion.div>

              <div className="flex justify-center gap-1 mb-8">
                {[...Array(5)].map((_, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.05 }}>
                    <Star className="text-primary fill-primary" size={16} />
                  </motion.div>
                ))}
              </div>

              <p className="text-foreground text-lg md:text-2xl leading-relaxed italic mb-10 font-display max-w-2xl mx-auto">"{t.text}"</p>

              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 rounded-full gold-gradient-bg flex items-center justify-center text-primary-foreground font-display font-bold text-sm shadow-lg shadow-primary/20">
                  {t.name.charAt(0)}
                </div>
                <div className="text-left">
                  <p className="text-foreground font-semibold tracking-wide text-sm">{t.name}</p>
                  <p className="text-primary/60 text-xs mt-0.5 tracking-wider uppercase">{t.vehicle}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-6 mt-12">
          <motion.button
            onClick={resetInterval(prev)}
            className="w-12 h-12 rounded-full glass-ultra flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all duration-300"
            aria-label="Previous testimonial"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronLeft size={18} />
          </motion.button>
          <div className="flex gap-0">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={resetInterval(() => { setDirection(i > current ? 1 : -1); setCurrent(i); })}
                className="w-12 h-12 inline-flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary rounded-full"
                aria-label={`Go to testimonial ${i + 1}`}
              >
                <span
                  aria-hidden="true"
                  className={`block rounded-full transition-all duration-500 ${i === current ? "bg-primary w-8 h-2.5 shadow-lg shadow-primary/30" : "bg-muted-foreground/15 w-2.5 h-2.5"}`}
                />
              </button>
            ))}
          </div>
          <motion.button
            onClick={resetInterval(next)}
            className="w-12 h-12 rounded-full glass-ultra flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all duration-300"
            aria-label="Next testimonial"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronRight size={18} />
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
